import { localize, readAppLanguage } from '../../../i18n';
import SingleLineText from '../../../components/ui/SingleLineText';
import React from 'react';
import { Box, Typography, Collapse, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';

const toggleGroupSx = { '& .MuiToggleButtonGroup-grouped': { border: 'none', borderRadius: '12px !important', mx: 0.3 } };
const toggleBtnSx = {
    textTransform: 'none', fontWeight: 600, fontSize: '1.05rem', py: 0.8,
    '&.Mui-selected': { bgcolor: '#ffe566 !important', color: '#5a4a00' },
    '&:not(.Mui-selected)': { bgcolor: '#f3f4f6', color: '#666' },
};

/**
 * 집요한 알림 sub-section — 등록/수정 확인 화면과 EditSnoozeSheet에서 공통 사용.
 */
export default function SnoozeSection({ form, setForm, hideToggle = false }) {
    const interval = form.reminderIntervalMinutes;
    const max = form.maxReminderMinutes;
    const intervalIsPreset = [10, 30, 60].includes(interval);
    const maxIsPreset = [60, 120, 360].includes(max);
    const totalCount = (interval > 0 && max > 0) ? Math.floor(max / interval) + 1 : 0;
    const hours = Math.floor(max / 60);
    const mins = max % 60;
    const duration = hours > 0
        ? (mins > 0 ? localize("{v0}시간 {v1}분", { v0: hours, v1: mins }) : localize("{v0}시간", { v0: hours }))
        : localize("{v0}분", { v0: mins });

    return (
        <Box>
            {!hideToggle && (
            <Box
                role="button"
                tabIndex={0}
                onClick={() => setForm(prev => ({ ...prev, reminderEnabled: !prev.reminderEnabled }))}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.2,
                    p: 1.3,
                    mb: form.reminderEnabled ? 2 : 0,
                    borderRadius: 3,
                    bgcolor: form.reminderEnabled ? '#fffbeb' : '#f9fafb',
                    border: form.reminderEnabled ? '1px solid #fde68a' : '1px solid #e5e7eb',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:active': { bgcolor: form.reminderEnabled ? '#fff3c4' : '#f3f4f6' },
                }}
            >
                <Box>
                    <SingleLineText sx={{ fontSize: '1.02rem', fontWeight: 900, color: '#1a1a1a' }}>{localize("다시 알림")}</SingleLineText>
                    <Typography sx={{ fontSize: '0.9rem', color: '#777', mt: 0.3 }}>
                        {form.reminderEnabled
                            ? localize("확인할 때까지 다시 알려드려요")
                            : localize("첫 알림 한 번만 보내요")}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        px: 1.15,
                        py: 0.55,
                        borderRadius: '999px',
                        bgcolor: form.reminderEnabled ? '#ffe566' : '#fff',
                        color: form.reminderEnabled ? '#5a4a00' : '#777',
                        border: form.reminderEnabled ? '1px solid #e6c800' : '1px solid #d1d5db',
                        fontSize: '0.88rem',
                        fontWeight: 900,
                        flexShrink: 0,
                    }}
                >
                    {form.reminderEnabled ? localize("켜짐") : localize("꺼짐")}
                </Box>
            </Box>
            )}

            <Collapse in={form.reminderEnabled}>
                <Box>
                    {/* 알림 간격 */}
                    <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#444', mb: 1 }}>{localize("알림 간격")}</SingleLineText>
                    <ToggleButtonGroup
                        value={intervalIsPreset ? interval : 'custom'}
                        exclusive
                        onChange={(_, val) => {
                            if (val === null) return;
                            const newInterval = val === 'custom' ? '' : val;
                            setForm(prev => ({
                                ...prev,
                                reminderIntervalMinutes: newInterval,
                                maxReminderMinutes: (newInterval > 0 && prev.maxReminderMinutes > 0 && prev.maxReminderMinutes < newInterval)
                                    ? newInterval : prev.maxReminderMinutes,
                            }));
                        }}
                        fullWidth size="small" sx={{ ...toggleGroupSx, mb: 1 }}
                    >
                        <ToggleButton value={10} sx={toggleBtnSx}>{localize("10분")}</ToggleButton>
                        <ToggleButton value={30} sx={toggleBtnSx}>{localize("30분")}</ToggleButton>
                        <ToggleButton value={60} sx={toggleBtnSx}>{localize("60분")}</ToggleButton>
                        <ToggleButton value={'custom'} sx={toggleBtnSx}>{localize("직접")}</ToggleButton>
                    </ToggleButtonGroup>
                    {!intervalIsPreset && (
                        <TextField
                            type="number" fullWidth size="small" placeholder={localize("1~360분")}
                            value={interval}
                            onChange={(e) => {
                                const num = Number(e.target.value);
                                // 음수/0/NaN 거름 + 360분(6시간) 초과는 clamp
                                const newInterval = (Number.isFinite(num) && num > 0) ? Math.min(Math.floor(num), 360) : '';
                                setForm(prev => ({
                                    ...prev,
                                    reminderIntervalMinutes: newInterval,
                                    maxReminderMinutes: (newInterval > 0 && prev.maxReminderMinutes > 0 && prev.maxReminderMinutes < newInterval)
                                        ? newInterval : prev.maxReminderMinutes,
                                }));
                            }}
                            onKeyDown={(e) => {
                                if (['-', '+', 'e', 'E', '.'].includes(e.key)) e.preventDefault();
                            }}
                            inputProps={{ min: 1, max: 360, inputMode: 'numeric', pattern: '[0-9]*' }}
                            helperText={localize("1분 ~ 6시간(360분)까지")}
                            sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                        />
                    )}

                    {/* 최대 시간 */}
                    <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#444', mt: 2, mb: 1 }}>{localize("최대 시간")}</SingleLineText>
                    <ToggleButtonGroup
                        value={maxIsPreset ? max : 'custom'}
                        exclusive
                        onChange={(_, val) => {
                            if (val === null) return;
                            const newMax = val === 'custom' ? '' : val;
                            if (newMax > 0 && interval > 0 && newMax < interval) return;
                            setForm(prev => ({ ...prev, maxReminderMinutes: newMax }));
                        }}
                        fullWidth size="small" sx={{ ...toggleGroupSx, mb: 1 }}
                    >
                        <ToggleButton value={60} disabled={interval > 60} sx={toggleBtnSx}>{localize("1시간")}</ToggleButton>
                        <ToggleButton value={120} disabled={interval > 120} sx={toggleBtnSx}>{localize("2시간")}</ToggleButton>
                        <ToggleButton value={360} sx={toggleBtnSx}>{localize("6시간")}</ToggleButton>
                        <ToggleButton value={'custom'} sx={toggleBtnSx}>{localize("직접")}</ToggleButton>
                    </ToggleButtonGroup>
                    {!maxIsPreset && (
                        <TextField
                            type="number" fullWidth size="small" placeholder={localize("1~720분")}
                            value={max}
                            onChange={(e) => {
                                const num = Number(e.target.value);
                                // 1~720분(12시간) clamp
                                const val = (Number.isFinite(num) && num > 0) ? Math.min(Math.floor(num), 720) : '';
                                setForm(prev => ({ ...prev, maxReminderMinutes: val }));
                            }}
                            onKeyDown={(e) => {
                                if (['-', '+', 'e', 'E', '.'].includes(e.key)) e.preventDefault();
                            }}
                            error={!!(max && interval > 0 && max < interval)}
                            inputProps={{ min: 1, max: 720, inputMode: 'numeric', pattern: '[0-9]*' }}
                            helperText={
                                max && interval > 0 && max < interval
                                    ? localize("알림 간격({v0}분) 이상으로 입력해주세요", { v0: interval })
                                    : localize("1분 ~ 12시간(720분)까지")
                            }
                            sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                        />
                    )}

                    {/* 시각적 요약 — 7세 친화: 단계별로 풀어서 안내 */}
                    {interval > 0 && max > 0 && (
                        <Box sx={{
                            mt: 2, p: 1.8, borderRadius: 3,
                            bgcolor: '#fffbe6', border: '1px solid #fde68a',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2 }}>
                                <Box sx={{
                                    width: 32, height: 32, borderRadius: '50%',
                                    bgcolor: '#ffe566', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    <NotificationsActiveOutlinedIcon sx={{ fontSize: 18, color: '#5a4a00' }} />
                                </Box>
                                <SingleLineText sx={{ fontSize: '1rem', fontWeight: 800, color: '#5a4a00' }}>{localize("이렇게 알려드릴게요")}</SingleLineText>
                            </Box>
                            <Box sx={{ pl: 0.5 }}>
                                <SingleLineText sx={{ fontSize: '0.95rem', color: '#5a4a00', lineHeight: 1.7, mb: 0.3, ...(readAppLanguage() !== 'ko' && { whiteSpace: 'normal', overflowWrap: 'anywhere' }) }}>{localize("1. 약 먹을 시간에 첫 알림이 가요")}</SingleLineText>
                                <Typography sx={{ fontSize: '0.95rem', color: '#5a4a00', lineHeight: 1.7, mb: 0.3 }}>{localize("2. 못 드시면 ")}<b>{interval}{localize("분")}</b>{localize(" 뒤 다시 알려드려요")}</Typography>
                                <Typography sx={{ fontSize: '0.95rem', color: '#5a4a00', lineHeight: 1.7 }}>
                                    3. <b>{duration}</b>{localize(" 동안 최대 ")}<b>{totalCount}{localize("번")}</b>{localize("까지 알림이 가요")}</Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
}
